import React, { useState, useContext, useEffect, useRef } from "react";
import { UserContext } from "../../../UserContext";
import Conclusion from "./ConclusionPopup";
import {
  // FaCheck,
  FaArrowRight,
  FaArrowLeft,
  FaChevronLeft,
  FaChevronRight,
} from "react-icons/fa";
import Panda from "../../../assets/Panda4.png";
import { Smile } from "lucide-react";
import { motion } from "framer-motion";
import { IoClose, IoRadioButtonOff } from "react-icons/io5";
// import posthog from "posthog-js";
import { usePostHog } from "posthog-js/react";
import { Listbox } from "@headlessui/react";
import { Check, ChevronDown } from "lucide-react";
import { usePopupWindowCloseHandler } from "../../../utils/useWindowCloseHandler.js";

const ActionPopup = ({
  summary,
  options,
  newSession,
  session_Goal,
  userId,
  subsectionId,
  onSessionComplete,
}) => {
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedOptions, setSelectedOptions] = useState([]);
  const [optionList, setOptionList] = useState(
    Array.isArray(options) ? options : []
  );
  const [isAllSelected, setIsAllSelected] = useState(false); // eslint-disable-line no-unused-vars
  const [newOption, setNewOption] = useState("");
  const [trackingFrequencies, setTrackingFrequencies] = useState({});
  const [conclutionPopup, setConclutionPopup] = useState(false);
  const [sessionGoal, setSessionGoal] = useState(""); // eslint-disable-line no-unused-vars
  const [actionData, setActionData] = useState("");
  const [insightData, setInsightData] = useState("");
  const [summeryData, setSummeryData] = useState("");
  const [conclusionData, setConclusionData] = useState("");
  const [thankYouData, setThankYouData] = useState("");
  const [accountability, setAccountability] = useState({});
  const apiBaseURL = process.env.REACT_APP_BACKEND_API_URL;
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [insightFromUser, setInsightFromUser] = useState("");
  const [sessionStoryId, setSessionStoryId] = useState("");
  const { user } = useContext(UserContext);

  // Mobile slider states
  const [currentSlide, setCurrentSlide] = useState(0);
  const [touchStart, setTouchStart] = useState(null);
  const [touchEnd, setTouchEnd] = useState(null);
  const sliderRef = useRef(null);

  usePopupWindowCloseHandler(
    newSession,
    user?.user_id,
    apiBaseURL,
    !conclutionPopup
  );

  const accountabilityOptions = [
    { value: "", label: "Select Accountability Partner" },
    { value: "no-one", label: "I'll go solo – I trust myself" },
    { value: "someone-else", label: "Someone special" },
  ];

  const handleOptionChange = (option) => {
    setIsAllSelected(false);
    if (selectedOptions.includes(option)) {
      setSelectedOptions(selectedOptions.filter((item) => item !== option));
      const newTracking = { ...trackingFrequencies };
      delete newTracking[option];
      setTrackingFrequencies(newTracking);

      // Also remove accountability when option is deselected
      const newAccountability = { ...accountability };
      delete newAccountability[option];
      setAccountability(newAccountability);
    } else {
      setSelectedOptions([...selectedOptions, option]);
    }

    // Reset slider to first slide when options change
    setCurrentSlide(0);
  };

  const handleAccountabilityChange = (option, value) => {
    setAccountability((prev) => ({
      ...prev,
      [option]: value,
    }));
  };

  // Touch handlers for mobile swipe
  const handleTouchStart = (e) => {
    setTouchEnd(null);
    setTouchStart(e.targetTouches[0].clientX);
  };

  const handleTouchMove = (e) => {
    setTouchEnd(e.targetTouches[0].clientX);
  };

  const handleTouchEnd = () => {
    if (!touchStart || !touchEnd) return;

    const distance = touchStart - touchEnd;
    const isLeftSwipe = distance > 50;
    const isRightSwipe = distance < -50;

    if (isLeftSwipe && currentSlide < selectedOptions.length - 1) {
      setCurrentSlide(currentSlide + 1);
    }
    if (isRightSwipe && currentSlide > 0) {
      setCurrentSlide(currentSlide - 1);
    }
  };

  // Navigation functions
  const nextSlide = () => {
    if (currentSlide < selectedOptions.length - 1) {
      setCurrentSlide(currentSlide + 1);
    }
  };

  const prevSlide = () => {
    if (currentSlide > 0) {
      setCurrentSlide(currentSlide - 1);
    }
  };

  const goToSlide = (index) => {
    setCurrentSlide(index);
  };

  const posthog = usePostHog();
  useEffect(() => {
    console.log("useEffect triggered");
    console.log(
      posthog.capture("Action_popup", { focus: "what u want to focus on" })
    );
    console.log("posthog.capture executed", window.location.href);
  }, [posthog]);

  const handleAddOption = () => {
    const trimmedOption = newOption.trim();

    if (
      trimmedOption !== "" &&
      !optionList.some(
        (opt) => opt.toLowerCase() === trimmedOption.toLowerCase()
      )
    ) {
      setOptionList([...optionList, trimmedOption]);
      setSelectedOptions([...selectedOptions, trimmedOption]);
      setNewOption("");
    }
  };

  const handleDeleteOption = (optionToDelete) => {
    setOptionList(optionList.filter((option) => option !== optionToDelete));
    setSelectedOptions(selectedOptions.filter((opt) => opt !== optionToDelete));

    // Also remove from tracking frequencies and accountability
    const newTracking = { ...trackingFrequencies };
    delete newTracking[optionToDelete];
    setTrackingFrequencies(newTracking);

    const newAccountability = { ...accountability };
    delete newAccountability[optionToDelete];
    setAccountability(newAccountability);

    // Adjust current slide if needed
    if (currentSlide >= selectedOptions.length - 1 && currentSlide > 0) {
      setCurrentSlide(currentSlide - 1);
    }
  };

  const formattedSelection = selectedOptions
    .map((option) => `${option} (${trackingFrequencies[option] || ""})`)
    .join(" , ");

  // Updated condition: Both frequency AND accountability must be selected for all options
  const isSubmitEnabled =
    selectedOptions.length > 0 &&
    selectedOptions.every((option) => trackingFrequencies[option]) &&
    selectedOptions.every(
      (option) => accountability[option] && accountability[option] !== ""
    );

  const handleSubmit = async () => {
    if (isSubmitting) return;
    setIsSubmitting(true);
    try {
      const formattedResponse = selectedOptions.map((option) => ({
        action: option,
        trackingFrequency: trackingFrequencies[option] || "",
        accountability: accountability[option]
          ? accountability[option] === "someone-else"
            ? {
                name:
                  document.querySelector(`input[placeholder="Their name"]`)
                    ?.value || "",
                email:
                  document.querySelector(
                    `input[placeholder="Their email (so we can nudge them gently)"]`
                  )?.value || "",
              }
            : {
                name: "Myself",
                email: "placeholder@email.com",
              }
          : null,
      }));
      const payload = {
        responselist: formattedResponse,
        session_id: newSession,
      };
      console.log("Payload to send:", payload);
      const response = await fetch(`${apiBaseURL}/action_responses`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(payload),
      });
      const data = await response.json();
      if (response.ok) {
        appendInSessionStep();
        genrateConclution();
      } else {
        console.error("Error in submission:", data.detail);
      }
    } catch (error) {
      console.error("Error during submission:", error);
    }
  };

  const appendInSessionStep = async () => {
    try {
      const payload = {
        coachee_response: formattedSelection,
        focus_popup_required: 1,
        record_from_focus_popup: 0,
        selected_focus_choice: 400,
        regenerate_with_intention: false,
        focus_options: "",
        alternate_focus_hypothesis: "",
        conversation_summary: "",
      };

      const response = await fetch(
        `${apiBaseURL}/users/${user.user_id}/session/${newSession}/step`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify(payload),
        }
      );
      const data = await response.json();
      if (response.ok) {
        console.log(data);
        setThankYouData(data.thank_you);
        setConclusionData(data.conclusion);
        setSummeryData(data.conclusion_summery);
      } else {
        console.error("Error in regeneration:", data.detail);
      }
    } catch (error) {
      console.error("Error during regeneration:", error);
    }
  };

  const genrateConclution = async () => {
    try {
      const response = await fetch(`${apiBaseURL}/conclusion/${newSession}`, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
        },
      });
      const data = await response.json();
      if (response.ok) {
        console.log(data);
        setConclutionPopup(true);
        setActionData(data.conclusion.session_actions);
        setInsightData(data.conclusion.session_insights);
        setSessionGoal(data.conclusion.session_goal);
        setInsightFromUser(data.question_response);
        setSessionStoryId(data.story_id);
        // setSummeryData(data.conclusion_summery);
      } else {
        console.error("Error in submission:", data.detail);
      }
    } catch (error) {
      console.error("Error during submission:", error);
    }
  };

  // Render individual option configuration
  const renderOptionConfiguration = (option, index, isMobile = false) => (
    <div
      key={index}
      className={`${isMobile ? "px-4 py-4 max-w-full" : "mt-6"} ${
        isMobile ? "overflow-hidden" : ""
      }`}
    >
      {/* Heading */}
      <h2
        className={`${
          isMobile ? "text-base leading-tight" : "text-lg md:text-xl"
        } font-light text-gray-700 mb-4 ${isMobile ? "break-words" : ""}`}
      >
        Let's create a plan for{" "}
        <span
          className={`${
            isMobile ? "text-base" : "text-lg md:text-xl"
          } font-medium text-gray-900 ${isMobile ? "break-words" : ""}`}
        >
          {option}
        </span>{" "}
        which makes the most sense.
      </h2>

      {/* Frequency Selection */}
      <p
        className={`${
          isMobile ? "text-sm" : "text-md md:text-lg"
        } font-medium text-gray-800 mb-3`}
      >
        How often will you do this?
      </p>
      <div
        className={`flex flex-wrap gap-2 mb-6 ${
          isMobile ? "justify-start" : ""
        }`}
      >
        {["Daily", "Weekly", "Monthly"].map((freq) => (
          <button
            key={freq}
            onClick={(e) => {
              e.stopPropagation();
              setTrackingFrequencies((prev) => ({
                ...prev,
                [option]: freq,
              }));
            }}
            className={`${
              isMobile ? "px-3 py-2 text-xs" : "px-3 py-1 text-md md:text-md"
            } rounded-lg font-medium transition-all flex-shrink-0
              ${
                trackingFrequencies[option] === freq
                  ? "bg-[#54B0AF] text-white"
                  : "bg-white border border-gray-400 hover:bg-gray-200 text-gray-700"
              }`}
          >
            {freq}
          </button>
        ))}
      </div>

      {/* Accountability Partner */}
      <p
        className={`${
          isMobile ? "text-sm" : "text-lg md:text-lg"
        } font-semibold text-gray-900 mb-3`}
      >
        Accountability Partner
      </p>

      {/* Mobile: Radio buttons */}
      {isMobile ? (
        <div className="space-y-4 mb-6">
          {accountabilityOptions.slice(1).map((opt) => (
            <label
              key={opt.value}
              className={`flex items-center p-4 rounded-xl border cursor-pointer transition-all ${
                accountability[option] === opt.value
                  ? "border-[#54B0AF] bg-[#54B0AF]/5"
                  : "border-gray-300 bg-white hover:bg-gray-50"
              }`}
              onClick={(e) => {
                e.stopPropagation();
                handleAccountabilityChange(option, opt.value);
              }}
            >
              <div
                className={`w-4 h-4 rounded-full border-2 flex items-center justify-center mr-3 ${
                  accountability[option] === opt.value
                    ? "border-[#54B0AF] bg-[#54B0AF]"
                    : "border-gray-300"
                }`}
              >
                {accountability[option] === opt.value && (
                  <div className="w-2 h-2 rounded-full bg-white"></div>
                )}
              </div>
              <span className="text-sm font-medium text-gray-900 leading-tight">
                {opt.label}
              </span>
            </label>
          ))}
        </div>
      ) : (
        /* Desktop: Dropdown */
        <Listbox
          value={accountability[option] || ""}
          onChange={(value) => handleAccountabilityChange(option, value)}
        >
          <div className="relative">
            <Listbox.Button className="relative w-full cursor-default rounded-xl border text-[#CACACA] border-gray-300 bg-gray-50 py-2 pl-4 pr-10 text-sm text-left shadow-sm focus:outline-none focus:ring-1 focus:ring-black">
              <span className="block truncate text-sm">
                {accountabilityOptions.find(
                  (o) => o.value === accountability[option]
                )?.label || "Select Accountability Partner"}
              </span>
              <span className="absolute inset-y-0 right-0 flex items-center pr-2 pointer-events-none">
                <ChevronDown className="h-4 w-4 text-[#CACACA]" />
              </span>
            </Listbox.Button>
            <Listbox.Options className="absolute z-10 mt-1 max-h-40 w-full overflow-auto rounded-xl bg-gray-100 py-1 text-sm shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none">
              {accountabilityOptions.map((opt) => (
                <Listbox.Option
                  key={opt.value}
                  value={opt.value}
                  className={({ active }) =>
                    `relative cursor-pointer select-none py-2 pl-10 pr-4 ${
                      active ? "bg-gray-200 text-gray-900" : "text-gray-700"
                    }`
                  }
                >
                  {({ selected }) => (
                    <>
                      <span
                        className={`block truncate ${
                          selected ? "font-medium" : "font-normal"
                        }`}
                      >
                        {opt.label}
                      </span>
                      {selected && (
                        <span className="absolute inset-y-0 left-0 flex items-center pl-3 text-[#54B0AF]">
                          <Check className="w-4 h-4" />
                        </span>
                      )}
                    </>
                  )}
                </Listbox.Option>
              ))}
            </Listbox.Options>
          </div>
        </Listbox>
      )}

      {/* Accountability Messages */}
      {accountability[option] === "no-one" && (
        <p
          className={`${
            isMobile ? "text-xs" : "text-sm"
          } text-gray-500 mt-4 flex items-center gap-1`}
        >
          <Smile
            className={`${
              isMobile ? "w-3 h-3" : "w-4 h-4"
            } text-gray-500 flex-shrink-0`}
          />
          <span className={isMobile ? "leading-tight" : ""}>
            No worries! We'll cheer you on with gentle reminders.
          </span>
        </p>
      )}
      {accountability[option] === "someone-else" && (
        <div
          className="mt-4 flex flex-col gap-3"
          onClick={(e) => e.stopPropagation()}
        >
          <input
            type="text"
            placeholder="Their name"
            className={`${
              isMobile ? "p-3 text-sm" : "p-1 text-md"
            } w-full rounded-lg border border-gray-300 placeholder-gray-400 focus:ring-2 focus:ring-black focus:border-black`}
            required
            onFocus={(e) => e.stopPropagation()}
            onTouchStart={(e) => e.stopPropagation()}
          />
          <input
            type="email"
            placeholder={
              isMobile
                ? "Their email"
                : "Their email (so we can nudge them gently)"
            }
            className={`${
              isMobile ? "p-3 text-sm" : "p-1 text-md"
            } w-full rounded-lg border border-gray-300 placeholder-gray-400 focus:ring-2 focus:ring-black focus:border-black`}
            required
            onFocus={(e) => e.stopPropagation()}
            onTouchStart={(e) => e.stopPropagation()}
          />
        </div>
      )}
    </div>
  );

  return (
    <>
      <style jsx>{`
        .custom-scrollbar::-webkit-scrollbar {
          width: 8px;
        }
        .custom-scrollbar::-webkit-scrollbar-track {
          background: #f1f1f1;
          border-radius: 4px;
        }
        .custom-scrollbar::-webkit-scrollbar-thumb {
          background: #c1c1c1;
          border-radius: 4px;
        }
        .custom-scrollbar::-webkit-scrollbar-thumb:hover {
          background: #a8a8a8;
        }

        /* Mobile scrollbar - completely hidden */
        .mobile-scroll::-webkit-scrollbar {
          display: none;
        }
        .mobile-scroll {
          -ms-overflow-style: none;
          scrollbar-width: none;
        }

        /* Mobile slider styles */
        .mobile-slider {
          display: flex;
          transition: transform 0.3s ease-in-out;
          transform: translateX(-${currentSlide * 100}%);
          width: 100%;
        }

        .slide {
          min-width: 100%;
          max-width: 100%;
          flex-shrink: 0;
          box-sizing: border-box;
          overflow: hidden;
        }

        /* Navigation styles - Enhanced and prettier */
        .slider-nav {
          position: absolute;
          top: 50%;
          transform: translateY(-50%);
          background: linear-gradient(135deg, #54b0af 0%, #48a3a2 100%);
          border: none;
          border-radius: 50%;
          width: 44px;
          height: 44px;
          display: flex;
          align-items: center;
          justify-content: center;
          cursor: pointer;
          z-index: 20;
          box-shadow: 0 4px 12px rgba(84, 176, 175, 0.3),
            0 2px 4px rgba(0, 0, 0, 0.1);
          transition: all 0.3s ease;
        }

        .slider-nav:hover:not(:disabled) {
          background: linear-gradient(135deg, #48a3a2 0%, #3d9291 100%);
          box-shadow: 0 6px 20px rgba(84, 176, 175, 0.4),
            0 4px 8px rgba(0, 0, 0, 0.15);
          transform: translateY(-50%) scale(1.05);
        }

        .slider-nav:active:not(:disabled) {
          transform: translateY(-50%) scale(0.95);
        }

        .slider-nav:disabled {
          background: linear-gradient(135deg, #d1d5db 0%, #9ca3af 100%);
          box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
          cursor: not-allowed;
          opacity: 0.6;
        }

        .slider-nav.prev {
          left: -16px;
        }

        .slider-nav.next {
          right: -16px;
        }

        /* Dots indicator */
        .slider-dots {
          display: flex;
          justify-content: center;
          gap: 8px;
          padding: 16px 0 12px 0;
          position: absolute;
          bottom: 0;
          left: 0;
          right: 0;
          background: linear-gradient(
            to top,
            rgba(255, 255, 255, 0.95) 0%,
            rgba(255, 255, 255, 0.8) 60%,
            transparent 100%
          );
          z-index: 15;
        }

        .slider-dot {
          width: 10px;
          height: 10px;
          border-radius: 50%;
          background-color: rgba(209, 213, 219, 0.8);
          cursor: pointer;
          transition: all 0.3s ease;
          border: 2px solid transparent;
        }

        .slider-dot.active {
          background-color: #54b0af;
          transform: scale(1.3);
          border-color: rgba(84, 176, 175, 0.3);
          box-shadow: 0 0 0 4px rgba(84, 176, 175, 0.2);
        }

        .slider-dot:hover:not(.active) {
          background-color: #9ca3af;
          transform: scale(1.15);
        }

        /* Slide counter */
        .slide-counter {
          position: absolute;
          // top: 16px;
          top: 8px;
          right: 12px;
          background: linear-gradient(
            135deg,
            rgba(0, 0, 0, 0.8) 0%,
            rgba(0, 0, 0, 0.6) 100%
          );
          color: white;
          font-size: 11px;
          padding: 6px 12px;
          border-radius: 16px;
          font-weight: 600;
          z-index: 20;
          box-shadow: 0 2px 8px rgba(0, 0, 0, 0.2);
          backdrop-filter: blur(4px);
        }

        /* Touch improvements for mobile */
        .mobile-slider-container {
          touch-action: pan-x;
          -webkit-overflow-scrolling: touch;
        }

        .mobile-content-area {
          touch-action: pan-y;
          -webkit-overflow-scrolling: touch;
        }

        /* Panda image responsive sizing */
        .panda-image {
          max-width: 100%;
          max-height: 100%;
          width: auto;
          height: auto;
          object-fit: contain;
        }

        /* Mobile specific panda sizing - MODIFIED FOR SMALLER SIZE */
        @media (max-width: 768px) {
          .panda-image-mobile {
            height: 60px;
            width: auto;
            max-width: 50px;
            max-height: 60px;
            object-fit: contain;
          }
        }

        /* Extra small screens - MODIFIED FOR SMALLER SIZE */
        @media (max-width: 480px) {
          .panda-image-mobile {
            height: 100px;
            width: auto;
            max-width: 105px;
            max-height: 80px;
            object-fit: contain;
          }
        }

        /* Desktop panda sizing */
        @media (min-width: 769px) {
          .panda-image-desktop {
            max-width: 350px;
            max-height: 500px;
            width: auto;
            height: auto;
            object-fit: contain;
          }
        }
      `}</style>

      {conclutionPopup ? (
        <Conclusion
          conclusion={conclusionData}
          thankyou={thankYouData}
          summary={summeryData}
          action={actionData}
          insight={insightData}
          yourInsight={insightFromUser}
          session_goal={session_Goal}
          newSession={newSession}
          sessionStoryId={sessionStoryId}
          userId={userId}
          subsectionId={subsectionId}
          onSessionComplete={onSessionComplete}
        />
      ) : (
        <div className="fixed inset-0 bg-gray-700 bg-opacity-80 flex justify-center items-center z-50 font-gotham p-2">
          <div className="bg-white mt-4 md:mt-10 rounded-2xl shadow-lg w-[95%] max-w-[1050px] overflow-hidden max-h-[95vh]">
            <div className="flex gap-3 ml-4 pt-2">
              {[...Array(3)].map((_, i) => (
                <div key={i} className="w-4 h-4 rounded-full"></div>
              ))}
            </div>

            {/* Page 1 - Option Selection */}
            {currentPage === 1 && (
              <div className="flex bg-white overflow-hidden scrollbar-hide">
                <div className="w-1/3 relative bg-gray-100 flex flex-col items-center justify-center">
                  <motion.img
                    src={Panda}
                    alt="Feedback Panda"
                    className="absolute bottom-0 left-0 panda-image panda-image-desktop"
                    initial={{ scale: 1 }}
                    animate={{ scale: 1 }}
                    exit={{ scale: 0.6, transition: { duration: 0.5 } }}
                  />
                </div>

                <div className="w-2/3 h-[600px] p-4 overflow-y-auto custom-scrollbar">
                  <h2 className="text-2xl font-medium text-gray-800">
                    Why don't you choose the focus areas of improvement?
                  </h2>
                  <p className="text-gray-500 text-lg font-medium mt-1">
                    (You can choose and add as many as you want.)
                  </p>

                  {/* Options */}
                  <div className="mt-6 space-y-4">
                    {optionList.map((option, index) => (
                      <div
                        key={index}
                        className={`flex items-center justify-between p-4 rounded-lg border cursor-pointer transition-all relative
                          ${
                            selectedOptions.includes(option)
                              ? "border-[#54B0AF] bg-gray-50"
                              : "border-gray-300 bg-white hover:bg-gray-50"
                          }`}
                        onClick={() => handleOptionChange(option)}
                      >
                        <div className="flex items-center">
                          <div
                            className={`w-6 h-6 rounded-md flex items-center justify-center transition-colors
                              ${
                                selectedOptions.includes(option)
                                  ? "bg-[#54B0AF] text-white"
                                  : "bg-gray-200"
                              }`}
                          >
                            {selectedOptions.includes(option) && (
                              <IoRadioButtonOff className="text-lg" />
                            )}
                          </div>
                          <span className="ml-3 font-medium text-lg rounded-xl text-gray-900">
                            {option}
                          </span>
                        </div>

                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDeleteOption(option);
                          }}
                          className="absolute -top-3 -right-2 bg-white border border-gray-400 rounded-full text-gray-500 hover:text-red-600 transition-all"
                        >
                          <IoClose className="w-5 h-5" />
                        </button>
                      </div>
                    ))}
                  </div>

                  {/* Add Custom Option */}
                  <div className="mt-4">
                    <input
                      type="text"
                      value={newOption}
                      onChange={(e) => setNewOption(e.target.value)}
                      className="w-full p-3 rounded-xl font-medium border border-[#54B0AF] text-lg placeholder-gray-400 placeholder:font-medium focus:outline-none focus:ring-1 focus:ring-[#54B0AF] focus:border-[#54B0AF]"
                      placeholder="Something else? Please type here..."
                    />
                    <button
                      onClick={handleAddOption}
                      className="p-2 text-[#54B0AF] font-medium hover:text-teal-600 transition-colors"
                    >
                      + Add Custom Action
                    </button>
                  </div>
                </div>
              </div>
            )}

            {/* Page 2 - Configuration */}
            {currentPage === 2 && (
              <div className="flex flex-col md:flex-row bg-white overflow-hidden rounded-lg">
                <motion.div
                  initial={{ scale: 1 }}
                  animate={{ scale: 1 }}
                  transition={{ duration: 0.5 }}
                  className="w-full md:w-1/3 px-4 sm:px-0 bg-gray-100 relative h-20 md:h-auto flex items-center justify-center"
                >
                  <motion.img
                    src={Panda}
                    alt="Feedback Panda"
                    className="absolute left-0 bottom-0 panda-image panda-image-mobile md:panda-image-desktop"
                    initial={{ scale: 1 }}
                    animate={{ scale: 1 }}
                    exit={{ scale: 0.6, transition: { duration: 0.5 } }}
                  />
                </motion.div>

                <div className="w-full md:w-2/3 px-4 md:px-4 h-[460px] md:h-[600px] relative">
                  {selectedOptions.length > 0 ? (
                    <>
                      {/* Desktop Version - Scrollable */}
                      <div className="hidden md:block h-full overflow-y-auto overflow-x-hidden custom-scrollbar scrollbar-hide max-h-[80vh]">
                        {selectedOptions.map((option, index) =>
                          renderOptionConfiguration(option, index, false)
                        )}
                      </div>

                      {/* Mobile Version - Slider */}
                      <div className="md:hidden h-full relative overflow-hidden">
                        {/* Slider Container */}
                        <div
                          className="overflow-hidden h-full w-full mobile-slider-container px-3"
                          onTouchStart={handleTouchStart}
                          onTouchMove={handleTouchMove}
                          onTouchEnd={handleTouchEnd}
                          ref={sliderRef}
                        >
                          <div className="mobile-slider h-full">
                            {selectedOptions.map((option, index) => (
                              <div
                                key={index}
                                className="slide w-full flex-shrink-0 box-border"
                              >
                                <div
                                  className="w-full h-full overflow-y-auto overflow-x-hidden py-6 mobile-scroll mobile-content-area"
                                  style={{ scrollBehavior: "smooth" }}
                                >
                                  {renderOptionConfiguration(
                                    option,
                                    index,
                                    true
                                  )}
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>

                        {/* Navigation Arrows - Mobile Only */}
                        {selectedOptions.length > 1 && (
                          <>
                            <button
                              className="slider-nav prev block md:hidden"
                              onClick={prevSlide}
                              disabled={currentSlide === 0}
                            >
                              <FaChevronLeft className="text-white text-sm" />
                            </button>
                            <button
                              className="slider-nav next block md:hidden"
                              onClick={nextSlide}
                              disabled={
                                currentSlide === selectedOptions.length - 1
                              }
                            >
                              <FaChevronRight className="text-white text-sm" />
                            </button>
                          </>
                        )}

                        {/* Slide Counter - Mobile Only */}
                        {/* {selectedOptions.length > 1 && (
                          <div className="slide-counter block md:hidden">
                            {currentSlide + 1} of {selectedOptions.length}
                          </div>
                        )} */}

                        {/* Dots Indicator - Mobile Only */}
                        {selectedOptions.length > 1 && (
                          <div className="slider-dots block md:hidden">
                            {selectedOptions.map((_, index) => (
                              <button
                                key={index}
                                className={`slider-dot ${
                                  index === currentSlide ? "active" : ""
                                }`}
                                onClick={() => goToSlide(index)}
                                aria-label={`Go to slide ${index + 1}`}
                              />
                            ))}
                          </div>
                        )}
                      </div>
                    </>
                  ) : (
                    <div className="flex items-center justify-center h-full">
                      <p className="text-red-500 text-lg">
                        No options selected!
                      </p>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Footer with Navigation */}
            <div className="flex justify-between items-center w-full p-3 md:p-4">
              <div className="flex gap-2">
                <span
                  className={`h-4 w-4 rounded-full transition-colors ${
                    currentPage === 1 ? "bg-[#54B0AF]" : "bg-gray-300"
                  }`}
                ></span>
                <span
                  className={`h-4 w-4 rounded-full transition-colors ${
                    currentPage === 2 ? "bg-[#54B0AF]" : "bg-gray-300"
                  }`}
                ></span>
              </div>

              <div className="flex gap-2 md:gap-4">
                {currentPage === 1 ? (
                  <button
                    onClick={() => setCurrentPage(2)}
                    disabled={selectedOptions.length === 0}
                    className={`px-4 md:px-6 py-2 rounded-full flex items-center justify-center gap-2 text-xl md:text-2xl transition-all
                      ${
                        selectedOptions.length > 0
                          ? "bg-[#54B0AF] text-white hover:bg-teal-600"
                          : "bg-[#CACACA] text-gray-600 opacity-50 cursor-not-allowed"
                      }`}
                  >
                    <FaArrowRight />
                  </button>
                ) : (
                  <div className="flex gap-2 md:gap-4">
                    <button
                      onClick={() => setCurrentPage(1)}
                      className="px-4 md:px-6 py-2 bg-gray-200 hover:bg-gray-300 text-gray-600 rounded-full flex text-lg md:text-xl items-center gap-2 transition-all"
                    >
                      <FaArrowLeft />
                    </button>

                    <button
                      onClick={handleSubmit}
                      disabled={isSubmitting || !isSubmitEnabled}
                      className={`px-4 md:px-6 py-2 md:py-3 rounded-full font-semibold transition-all text-sm md:text-base
                        ${
                          isSubmitting
                            ? "bg-gray-400 cursor-not-allowed text-white"
                            : isSubmitEnabled
                            ? "bg-[#54B0AF] text-white hover:bg-teal-600"
                            : "bg-[#CACACA] text-gray-600 opacity-50 cursor-not-allowed"
                        }`}
                    >
                      {isSubmitting ? "Submitting..." : "Submit"}
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default ActionPopup;

// import React, { useState, useContext, useEffect, useRef } from "react";
// import { UserContext } from "../../../UserContext";
// import Conclusion from "./ConclusionPopup";
// import {
//   FaCheck,
//   FaArrowRight,
//   FaArrowLeft,
//   FaChevronLeft,
//   FaChevronRight,
// } from "react-icons/fa";
// import Panda from "../../../assets/Panda4.png";
// import { Smile } from "lucide-react";
// import { motion } from "framer-motion";
// import { IoClose, IoRadioButtonOff } from "react-icons/io5";
// // import posthog from "posthog-js";
// import { usePostHog } from "posthog-js/react";
// import { Listbox } from "@headlessui/react";
// import { Check, ChevronDown } from "lucide-react";

// const ActionPopup = ({ summary, options, newSession, session_Goal }) => {
//   const [currentPage, setCurrentPage] = useState(1);
//   const [selectedOptions, setSelectedOptions] = useState([]);
//   const [optionList, setOptionList] = useState(
//     Array.isArray(options) ? options : []
//   );
//   const [isAllSelected, setIsAllSelected] = useState(false);
//   const [newOption, setNewOption] = useState("");
//   const [trackingFrequencies, setTrackingFrequencies] = useState({});
//   const [conclutionPopup, setConclutionPopup] = useState(false);
//   const [sessionGoal, setSessionGoal] = useState("");
//   const [actionData, setActionData] = useState("");
//   const [insightData, setInsightData] = useState("");
//   const [summeryData, setSummeryData] = useState("");
//   const [conclusionData, setConclusionData] = useState("");
//   const [thankYouData, setThankYouData] = useState("");
//   const [accountability, setAccountability] = useState({});
//   const apiBaseURL = process.env.REACT_APP_BACKEND_API_URL;
//   const [isSubmitting, setIsSubmitting] = useState(false);
//   const [insightFromUser, setInsightFromUser] = useState("");
//   const { user } = useContext(UserContext);

//   // Mobile slider states
//   const [currentSlide, setCurrentSlide] = useState(0);
//   const [touchStart, setTouchStart] = useState(null);
//   const [touchEnd, setTouchEnd] = useState(null);
//   const sliderRef = useRef(null);

//   const accountabilityOptions = [
//     { value: "", label: "Select Accountability Partner" },
//     { value: "no-one", label: "I'll go solo – I trust myself" },
//     { value: "someone-else", label: "Someone special" },
//   ];

//   const handleOptionChange = (option) => {
//     setIsAllSelected(false);
//     if (selectedOptions.includes(option)) {
//       setSelectedOptions(selectedOptions.filter((item) => item !== option));
//       const newTracking = { ...trackingFrequencies };
//       delete newTracking[option];
//       setTrackingFrequencies(newTracking);

//       // Also remove accountability when option is deselected
//       const newAccountability = { ...accountability };
//       delete newAccountability[option];
//       setAccountability(newAccountability);
//     } else {
//       setSelectedOptions([...selectedOptions, option]);
//     }

//     // Reset slider to first slide when options change
//     setCurrentSlide(0);
//   };

//   const handleAccountabilityChange = (option, value) => {
//     setAccountability((prev) => ({
//       ...prev,
//       [option]: value,
//     }));
//   };

//   // Touch handlers for mobile swipe
//   const handleTouchStart = (e) => {
//     setTouchEnd(null);
//     setTouchStart(e.targetTouches[0].clientX);
//   };

//   const handleTouchMove = (e) => {
//     setTouchEnd(e.targetTouches[0].clientX);
//   };

//   const handleTouchEnd = () => {
//     if (!touchStart || !touchEnd) return;

//     const distance = touchStart - touchEnd;
//     const isLeftSwipe = distance > 50;
//     const isRightSwipe = distance < -50;

//     if (isLeftSwipe && currentSlide < selectedOptions.length - 1) {
//       setCurrentSlide(currentSlide + 1);
//     }
//     if (isRightSwipe && currentSlide > 0) {
//       setCurrentSlide(currentSlide - 1);
//     }
//   };

//   // Navigation functions
//   const nextSlide = () => {
//     if (currentSlide < selectedOptions.length - 1) {
//       setCurrentSlide(currentSlide + 1);
//     }
//   };

//   const prevSlide = () => {
//     if (currentSlide > 0) {
//       setCurrentSlide(currentSlide - 1);
//     }
//   };

//   const goToSlide = (index) => {
//     setCurrentSlide(index);
//   };

//   const posthog = usePostHog();
//   useEffect(() => {
//     console.log("useEffect triggered");
//     console.log(
//       posthog.capture("Action_popup", { focus: "what u want to focus on" })
//     );
//     console.log("posthog.capture executed", window.location.href);
//   }, []);

//   const handleAddOption = () => {
//     const trimmedOption = newOption.trim();

//     if (
//       trimmedOption !== "" &&
//       !optionList.some(
//         (opt) => opt.toLowerCase() === trimmedOption.toLowerCase()
//       )
//     ) {
//       setOptionList([...optionList, trimmedOption]);
//       setSelectedOptions([...selectedOptions, trimmedOption]);
//       setNewOption("");
//     }
//   };

//   const handleDeleteOption = (optionToDelete) => {
//     setOptionList(optionList.filter((option) => option !== optionToDelete));
//     setSelectedOptions(selectedOptions.filter((opt) => opt !== optionToDelete));

//     // Also remove from tracking frequencies and accountability
//     const newTracking = { ...trackingFrequencies };
//     delete newTracking[optionToDelete];
//     setTrackingFrequencies(newTracking);

//     const newAccountability = { ...accountability };
//     delete newAccountability[optionToDelete];
//     setAccountability(newAccountability);

//     // Adjust current slide if needed
//     if (currentSlide >= selectedOptions.length - 1 && currentSlide > 0) {
//       setCurrentSlide(currentSlide - 1);
//     }
//   };

//   const formattedSelection = selectedOptions
//     .map((option) => `${option} (${trackingFrequencies[option] || ""})`)
//     .join(" , ");

//   // Updated condition: Both frequency AND accountability must be selected for all options
//   const isSubmitEnabled =
//     selectedOptions.length > 0 &&
//     selectedOptions.every((option) => trackingFrequencies[option]) &&
//     selectedOptions.every(
//       (option) => accountability[option] && accountability[option] !== ""
//     );

//   const handleSubmit = async () => {
//     if (isSubmitting) return;
//     setIsSubmitting(true);
//     try {
//       const formattedResponse = selectedOptions.map((option) => ({
//         action: option,
//         trackingFrequency: trackingFrequencies[option] || "",
//         accountability: accountability[option]
//           ? accountability[option] === "someone-else"
//             ? {
//                 name:
//                   document.querySelector(`input[placeholder="Their name"]`)
//                     ?.value || "",
//                 email:
//                   document.querySelector(
//                     `input[placeholder="Their email (so we can nudge them gently)"]`
//                   )?.value || "",
//               }
//             : {
//                 name: "Myself",
//                 email: "placeholder@email.com",
//               }
//           : null,
//       }));
//       const payload = {
//         responselist: formattedResponse,
//         session_id: newSession,
//       };
//       console.log("Payload to send:", payload);
//       const response = await fetch(`${apiBaseURL}/action_responses`, {
//         method: "POST",
//         headers: {
//           "Content-Type": "application/json",
//         },
//         body: JSON.stringify(payload),
//       });
//       const data = await response.json();
//       if (response.ok) {
//         appendInSessionStep();
//         genrateConclution();
//       } else {
//         console.error("Error in submission:", data.detail);
//       }
//     } catch (error) {
//       console.error("Error during submission:", error);
//     }
//   };

//   const appendInSessionStep = async () => {
//     try {
//       const payload = {
//         coachee_response: formattedSelection,
//         focus_popup_required: 1,
//         record_from_focus_popup: 0,
//         selected_focus_choice: 400,
//         regenerate_with_intention: false,
//         focus_options: "",
//         alternate_focus_hypothesis: "",
//         conversation_summary: "",
//       };

//       const response = await fetch(
//         `${apiBaseURL}/users/${user.user_id}/session/${newSession}/step`,
//         {
//           method: "POST",
//           headers: {
//             "Content-Type": "application/json",
//           },
//           body: JSON.stringify(payload),
//         }
//       );
//       const data = await response.json();
//       if (response.ok) {
//         console.log(data);
//         setThankYouData(data.thank_you);
//         setConclusionData(data.conclusion);
//         setSummeryData(data.conclusion_summery);
//       } else {
//         console.error("Error in regeneration:", data.detail);
//       }
//     } catch (error) {
//       console.error("Error during regeneration:", error);
//     }
//   };

//   const genrateConclution = async () => {
//     try {
//       const response = await fetch(`${apiBaseURL}/conclusion/${newSession}`, {
//         method: "GET",
//         headers: {
//           "Content-Type": "application/json",
//         },
//       });
//       const data = await response.json();
//       if (response.ok) {
//         console.log(data);
//         setConclutionPopup(true);
//         setActionData(data.conclusion.session_actions);
//         setInsightData(data.conclusion.session_insights);
//         setSessionGoal(data.conclusion.session_goal);
//         setInsightFromUser(data.question_response);
//       } else {
//         console.error("Error in submission:", data.detail);
//       }
//     } catch (error) {
//       console.error("Error during submission:", error);
//     }
//   };

//   // Render individual option configuration
//   const renderOptionConfiguration = (option, index, isMobile = false) => (
//     <div
//       key={index}
//       className={`${isMobile ? "px-4 py-4 max-w-full" : "mt-6"} ${
//         isMobile ? "overflow-hidden" : ""
//       }`}
//     >
//       {/* Heading */}
//       <h2
//         className={`${
//           isMobile ? "text-base leading-tight" : "text-lg md:text-xl"
//         } font-light text-gray-700 mb-4 ${isMobile ? "break-words" : ""}`}
//       >
//         Let's create a plan for{" "}
//         <span
//           className={`${
//             isMobile ? "text-base" : "text-lg md:text-xl"
//           } font-medium text-gray-900 ${isMobile ? "break-words" : ""}`}
//         >
//           {option}
//         </span>{" "}
//         which makes the most sense.
//       </h2>

//       {/* Frequency Selection */}
//       <p
//         className={`${
//           isMobile ? "text-sm" : "text-md md:text-lg"
//         } font-medium text-gray-800 mb-3`}
//       >
//         How often will you do this?
//       </p>
//       <div
//         className={`flex flex-wrap gap-2 mb-6 ${
//           isMobile ? "justify-start" : ""
//         }`}
//       >
//         {["Daily", "Weekly", "Monthly"].map((freq) => (
//           <button
//             key={freq}
//             onClick={(e) => {
//               e.stopPropagation();
//               setTrackingFrequencies((prev) => ({
//                 ...prev,
//                 [option]: freq,
//               }));
//             }}
//             className={`${
//               isMobile ? "px-3 py-2 text-xs" : "px-3 py-1 text-md md:text-md"
//             } rounded-lg font-medium transition-all flex-shrink-0
//               ${
//                 trackingFrequencies[option] === freq
//                   ? "bg-[#54B0AF] text-white"
//                   : "bg-white border border-gray-400 hover:bg-gray-200 text-gray-700"
//               }`}
//           >
//             {freq}
//           </button>
//         ))}
//       </div>

//       {/* Accountability Partner */}
//       <p
//         className={`${
//           isMobile ? "text-sm" : "text-lg md:text-lg"
//         } font-semibold text-gray-900 mb-3`}
//       >
//         Accountability Partner
//       </p>

//       {/* Mobile: Radio buttons */}
//       {isMobile ? (
//         <div className="space-y-4 mb-6">
//           {accountabilityOptions.slice(1).map((opt) => (
//             <label
//               key={opt.value}
//               className={`flex items-center p-4 rounded-xl border cursor-pointer transition-all ${
//                 accountability[option] === opt.value
//                   ? "border-[#54B0AF] bg-[#54B0AF]/5"
//                   : "border-gray-300 bg-white hover:bg-gray-50"
//               }`}
//               onClick={(e) => {
//                 e.stopPropagation();
//                 handleAccountabilityChange(option, opt.value);
//               }}
//             >
//               <div
//                 className={`w-4 h-4 rounded-full border-2 flex items-center justify-center mr-3 ${
//                   accountability[option] === opt.value
//                     ? "border-[#54B0AF] bg-[#54B0AF]"
//                     : "border-gray-300"
//                 }`}
//               >
//                 {accountability[option] === opt.value && (
//                   <div className="w-2 h-2 rounded-full bg-white"></div>
//                 )}
//               </div>
//               <span className="text-sm font-medium text-gray-900 leading-tight">
//                 {opt.label}
//               </span>
//             </label>
//           ))}
//         </div>
//       ) : (
//         /* Desktop: Dropdown */
//         <Listbox
//           value={accountability[option] || ""}
//           onChange={(value) => handleAccountabilityChange(option, value)}
//         >
//           <div className="relative">
//             <Listbox.Button className="relative w-full cursor-default rounded-xl border text-[#CACACA] border-gray-300 bg-gray-50 py-2 pl-4 pr-10 text-sm text-left shadow-sm focus:outline-none focus:ring-1 focus:ring-black">
//               <span className="block truncate text-sm">
//                 {accountabilityOptions.find(
//                   (o) => o.value === accountability[option]
//                 )?.label || "Select Accountability Partner"}
//               </span>
//               <span className="absolute inset-y-0 right-0 flex items-center pr-2 pointer-events-none">
//                 <ChevronDown className="h-4 w-4 text-[#CACACA]" />
//               </span>
//             </Listbox.Button>
//             <Listbox.Options className="absolute z-10 mt-1 max-h-40 w-full overflow-auto rounded-xl bg-gray-100 py-1 text-sm shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none">
//               {accountabilityOptions.map((opt) => (
//                 <Listbox.Option
//                   key={opt.value}
//                   value={opt.value}
//                   className={({ active }) =>
//                     `relative cursor-pointer select-none py-2 pl-10 pr-4 ${
//                       active ? "bg-gray-200 text-gray-900" : "text-gray-700"
//                     }`
//                   }
//                 >
//                   {({ selected }) => (
//                     <>
//                       <span
//                         className={`block truncate ${
//                           selected ? "font-medium" : "font-normal"
//                         }`}
//                       >
//                         {opt.label}
//                       </span>
//                       {selected && (
//                         <span className="absolute inset-y-0 left-0 flex items-center pl-3 text-[#54B0AF]">
//                           <Check className="w-4 h-4" />
//                         </span>
//                       )}
//                     </>
//                   )}
//                 </Listbox.Option>
//               ))}
//             </Listbox.Options>
//           </div>
//         </Listbox>
//       )}

//       {/* Accountability Messages */}
//       {accountability[option] === "no-one" && (
//         <p
//           className={`${
//             isMobile ? "text-xs" : "text-sm"
//           } text-gray-500 mt-4 flex items-center gap-1`}
//         >
//           <Smile
//             className={`${
//               isMobile ? "w-3 h-3" : "w-4 h-4"
//             } text-gray-500 flex-shrink-0`}
//           />
//           <span className={isMobile ? "leading-tight" : ""}>
//             No worries! We'll cheer you on with gentle reminders.
//           </span>
//         </p>
//       )}
//       {accountability[option] === "someone-else" && (
//         <div
//           className="mt-4 flex flex-col gap-3"
//           onClick={(e) => e.stopPropagation()}
//         >
//           <input
//             type="text"
//             placeholder="Their name"
//             className={`${
//               isMobile ? "p-3 text-sm" : "p-1 text-md"
//             } w-full rounded-lg border border-gray-300 placeholder-gray-400 focus:ring-2 focus:ring-black focus:border-black`}
//             required
//             onFocus={(e) => e.stopPropagation()}
//             onTouchStart={(e) => e.stopPropagation()}
//           />
//           <input
//             type="email"
//             placeholder={
//               isMobile
//                 ? "Their email"
//                 : "Their email (so we can nudge them gently)"
//             }
//             className={`${
//               isMobile ? "p-3 text-sm" : "p-1 text-md"
//             } w-full rounded-lg border border-gray-300 placeholder-gray-400 focus:ring-2 focus:ring-black focus:border-black`}
//             required
//             onFocus={(e) => e.stopPropagation()}
//             onTouchStart={(e) => e.stopPropagation()}
//           />
//         </div>
//       )}
//     </div>
//   );

//   return (
//     <>
//       <style jsx>{`
//         .custom-scrollbar::-webkit-scrollbar {
//           width: 8px;
//         }
//         .custom-scrollbar::-webkit-scrollbar-track {
//           background: #f1f1f1;
//           border-radius: 4px;
//         }
//         .custom-scrollbar::-webkit-scrollbar-thumb {
//           background: #c1c1c1;
//           border-radius: 4px;
//         }
//         .custom-scrollbar::-webkit-scrollbar-thumb:hover {
//           background: #a8a8a8;
//         }

//         /* Mobile scrollbar - completely hidden */
//         .mobile-scroll::-webkit-scrollbar {
//           display: none;
//         }
//         .mobile-scroll {
//           -ms-overflow-style: none;
//           scrollbar-width: none;
//         }

//         /* Mobile slider styles */
//         .mobile-slider {
//           display: flex;
//           transition: transform 0.3s ease-in-out;
//           transform: translateX(-${currentSlide * 100}%);
//           width: 100%;
//         }

//         .slide {
//           min-width: 100%;
//           max-width: 100%;
//           flex-shrink: 0;
//           box-sizing: border-box;
//           overflow: hidden;
//         }

//         /* Navigation styles - Enhanced and prettier */
//         .slider-nav {
//           position: absolute;
//           top: 50%;
//           transform: translateY(-50%);
//           background: linear-gradient(135deg, #54b0af 0%, #48a3a2 100%);
//           border: none;
//           border-radius: 50%;
//           width: 44px;
//           height: 44px;
//           display: flex;
//           align-items: center;
//           justify-content: center;
//           cursor: pointer;
//           z-index: 20;
//           box-shadow: 0 4px 12px rgba(84, 176, 175, 0.3),
//             0 2px 4px rgba(0, 0, 0, 0.1);
//           transition: all 0.3s ease;
//         }

//         .slider-nav:hover:not(:disabled) {
//           background: linear-gradient(135deg, #48a3a2 0%, #3d9291 100%);
//           box-shadow: 0 6px 20px rgba(84, 176, 175, 0.4),
//             0 4px 8px rgba(0, 0, 0, 0.15);
//           transform: translateY(-50%) scale(1.05);
//         }

//         .slider-nav:active:not(:disabled) {
//           transform: translateY(-50%) scale(0.95);
//         }

//         .slider-nav:disabled {
//           background: linear-gradient(135deg, #d1d5db 0%, #9ca3af 100%);
//           box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
//           cursor: not-allowed;
//           opacity: 0.6;
//         }

//         .slider-nav.prev {
//           left: -16px;
//         }

//         .slider-nav.next {
//           right: -16px;
//         }

//         /* Dots indicator */
//         .slider-dots {
//           display: flex;
//           justify-content: center;
//           gap: 8px;
//           padding: 16px 0 12px 0;
//           position: absolute;
//           bottom: 0;
//           left: 0;
//           right: 0;
//           background: linear-gradient(
//             to top,
//             rgba(255, 255, 255, 0.95) 0%,
//             rgba(255, 255, 255, 0.8) 60%,
//             transparent 100%
//           );
//           z-index: 15;
//         }

//         .slider-dot {
//           width: 10px;
//           height: 10px;
//           border-radius: 50%;
//           background-color: rgba(209, 213, 219, 0.8);
//           cursor: pointer;
//           transition: all 0.3s ease;
//           border: 2px solid transparent;
//         }

//         .slider-dot.active {
//           background-color: #54b0af;
//           transform: scale(1.3);
//           border-color: rgba(84, 176, 175, 0.3);
//           box-shadow: 0 0 0 4px rgba(84, 176, 175, 0.2);
//         }

//         .slider-dot:hover:not(.active) {
//           background-color: #9ca3af;
//           transform: scale(1.15);
//         }

//         /* Slide counter */
//         .slide-counter {
//           position: absolute;
//           // top: 16px;
//           top: 8px;
//           right: 12px;
//           background: linear-gradient(
//             135deg,
//             rgba(0, 0, 0, 0.8) 0%,
//             rgba(0, 0, 0, 0.6) 100%
//           );
//           color: white;
//           font-size: 11px;
//           padding: 6px 12px;
//           border-radius: 16px;
//           font-weight: 600;
//           z-index: 20;
//           box-shadow: 0 2px 8px rgba(0, 0, 0, 0.2);
//           backdrop-filter: blur(4px);
//         }

//         /* Touch improvements for mobile */
//         .mobile-slider-container {
//           touch-action: pan-x;
//           -webkit-overflow-scrolling: touch;
//         }

//         .mobile-content-area {
//           touch-action: pan-y;
//           -webkit-overflow-scrolling: touch;
//         }

//         /* Panda image responsive sizing */
//         .panda-image {
//           max-width: 100%;
//           max-height: 100%;
//           width: auto;
//           height: auto;
//           object-fit: contain;
//         }

//         /* Mobile specific panda sizing */
//         @media (max-width: 768px) {
//           .panda-image-mobile {
//             max-width: 120px;
//             max-height: 120px;
//             width: auto;
//             height: auto;
//             object-fit: contain;
//           }
//         }

//         /* Extra small screens */
//         @media (max-width: 480px) {
//           .panda-image-mobile {
//             max-width: 100px;
//             max-height: 100px;
//           }
//         }

//         /* Desktop panda sizing */
//         @media (min-width: 769px) {
//           .panda-image-desktop {
//             max-width: 280px;
//             max-height: 400px;
//             width: auto;
//             height: auto;
//             object-fit: contain;
//           }
//         }
//       `}</style>

//       {conclutionPopup ? (
//         <Conclusion
//           conclusion={conclusionData}
//           thankyou={thankYouData}
//           summary={summeryData}
//           action={actionData}
//           insight={insightData}
//           yourInsight={insightFromUser}
//           session_goal={session_Goal}
//           newSession={newSession}
//         />
//       ) : (
//         <div className="fixed inset-0 bg-gray-700 bg-opacity-80 flex justify-center items-center z-50 font-gotham p-2">
//           <div className="bg-white mt-4 md:mt-10 rounded-2xl shadow-lg w-[95%] max-w-[1050px] overflow-hidden max-h-[95vh]">
//             <div className="flex gap-3 ml-4 pt-2">
//               {[...Array(3)].map((_, i) => (
//                 <div key={i} className="w-4 h-4 rounded-full"></div>
//               ))}
//             </div>

//             {/* Page 1 - Option Selection */}
//             {currentPage === 1 && (
//               <div className="flex bg-white overflow-hidden">
//                 <div className="w-1/3 relative bg-gray-100 flex flex-col items-center justify-center">
//                   <motion.img
//                     src={Panda}
//                     alt="Feedback Panda"
//                     className="absolute bottom-0 panda-image panda-image-desktop"
//                     initial={{ scale: 1 }}
//                     animate={{ scale: 1 }}
//                     exit={{ scale: 0.6, transition: { duration: 0.5 } }}
//                   />
//                 </div>

//                 <div className="w-2/3 h-[600px] p-4 overflow-y-auto custom-scrollbar">
//                   <h2 className="text-2xl font-medium text-gray-800">
//                     Why don't you choose the focus areas of improvement?
//                   </h2>
//                   <p className="text-gray-500 text-lg font-medium mt-1">
//                     (You can choose and add as many as you want.)
//                   </p>

//                   {/* Options */}
//                   <div className="mt-6 space-y-4">
//                     {optionList.map((option, index) => (
//                       <div
//                         key={index}
//                         className={`flex items-center justify-between p-4 rounded-lg border cursor-pointer transition-all relative
//                           ${
//                             selectedOptions.includes(option)
//                               ? "border-[#54B0AF] bg-gray-50"
//                               : "border-gray-300 bg-white hover:bg-gray-50"
//                           }`}
//                         onClick={() => handleOptionChange(option)}
//                       >
//                         <div className="flex items-center">
//                           <div
//                             className={`w-6 h-6 rounded-md flex items-center justify-center transition-colors
//                               ${
//                                 selectedOptions.includes(option)
//                                   ? "bg-[#54B0AF] text-white"
//                                   : "bg-gray-200"
//                               }`}
//                           >
//                             {selectedOptions.includes(option) && (
//                               <IoRadioButtonOff className="text-lg" />
//                             )}
//                           </div>
//                           <span className="ml-3 font-medium text-lg rounded-xl text-gray-900">
//                             {option}
//                           </span>
//                         </div>

//                         <button
//                           onClick={(e) => {
//                             e.stopPropagation();
//                             handleDeleteOption(option);
//                           }}
//                           className="absolute -top-3 -right-2 bg-white border border-gray-400 rounded-full text-gray-500 hover:text-red-600 transition-all"
//                         >
//                           <IoClose className="w-5 h-5" />
//                         </button>
//                       </div>
//                     ))}
//                   </div>

//                   {/* Add Custom Option */}
//                   <div className="mt-4">
//                     <input
//                       type="text"
//                       value={newOption}
//                       onChange={(e) => setNewOption(e.target.value)}
//                       className="w-full p-3 rounded-xl font-medium border border-[#54B0AF] text-lg placeholder-gray-400 placeholder:font-medium focus:outline-none focus:ring-1 focus:ring-[#54B0AF] focus:border-[#54B0AF]"
//                       placeholder="Something else? Please type here..."
//                     />
//                     <button
//                       onClick={handleAddOption}
//                       className="p-2 text-[#54B0AF] font-medium hover:text-teal-600 transition-colors"
//                     >
//                       + Add Custom Action
//                     </button>
//                   </div>
//                 </div>
//               </div>
//             )}

//             {/* Page 2 - Configuration */}
//             {currentPage === 2 && (
//               <div className="flex flex-col md:flex-row bg-white overflow-hidden rounded-lg">
//                 <motion.div
//                   initial={{ scale: 1 }}
//                   animate={{ scale: 1 }}
//                   transition={{ duration: 0.5 }}
//                   className="w-full md:w-1/3 bg-gray-100 relative h-36 md:h-auto flex items-center justify-center"
//                 >
//                   <motion.img
//                     src={Panda}
//                     alt="Feedback Panda"
//                     className="absolute left-0 bottom-0 panda-image panda-image-mobile md:panda-image-desktop"
//                     initial={{ scale: 1 }}
//                     animate={{ scale: 1 }}
//                     exit={{ scale: 0.6, transition: { duration: 0.5 } }}
//                   />
//                 </motion.div>

//                 <div className="w-full md:w-2/3 px-4 md:px-4 h-[380px] md:h-[600px] relative">
//                   {selectedOptions.length > 0 ? (
//                     <>
//                       {/* Desktop Version - Scrollable */}
//                       <div className="hidden md:block h-full overflow-y-auto overflow-x-hidden custom-scrollbar scrollbar-hide max-h-[80vh]">
//                         {selectedOptions.map((option, index) =>
//                           renderOptionConfiguration(option, index, false)
//                         )}
//                       </div>

//                       {/* Mobile Version - Slider */}
//                       <div className="md:hidden h-full relative overflow-hidden">
//                         {/* Slider Container */}
//                         <div
//                           className="overflow-hidden h-full w-full mobile-slider-container px-3"
//                           onTouchStart={handleTouchStart}
//                           onTouchMove={handleTouchMove}
//                           onTouchEnd={handleTouchEnd}
//                           ref={sliderRef}
//                         >
//                           <div className="mobile-slider h-full">
//                             {selectedOptions.map((option, index) => (
//                               <div
//                                 key={index}
//                                 className="slide w-full flex-shrink-0 box-border"
//                               >
//                                 <div
//                                   className="w-full h-full overflow-y-auto overflow-x-hidden py-6 mobile-scroll mobile-content-area"
//                                   style={{ scrollBehavior: "smooth" }}
//                                 >
//                                   {renderOptionConfiguration(
//                                     option,
//                                     index,
//                                     true
//                                   )}
//                                 </div>
//                               </div>
//                             ))}
//                           </div>
//                         </div>

//                         {/* Navigation Arrows - Mobile Only */}
//                         {selectedOptions.length > 1 && (
//                           <>
//                             <button
//                               className="slider-nav prev block md:hidden"
//                               onClick={prevSlide}
//                               disabled={currentSlide === 0}
//                             >
//                               <FaChevronLeft className="text-white text-sm" />
//                             </button>
//                             <button
//                               className="slider-nav next block md:hidden"
//                               onClick={nextSlide}
//                               disabled={
//                                 currentSlide === selectedOptions.length - 1
//                               }
//                             >
//                               <FaChevronRight className="text-white text-sm" />
//                             </button>
//                           </>
//                         )}

//                         {/* Slide Counter - Mobile Only */}
//                         {/* {selectedOptions.length > 1 && (
//                           <div className="slide-counter block md:hidden">
//                             {currentSlide + 1} of {selectedOptions.length}
//                           </div>
//                         )} */}

//                         {/* Dots Indicator - Mobile Only */}
//                         {selectedOptions.length > 1 && (
//                           <div className="slider-dots block md:hidden">
//                             {selectedOptions.map((_, index) => (
//                               <button
//                                 key={index}
//                                 className={`slider-dot ${
//                                   index === currentSlide ? "active" : ""
//                                 }`}
//                                 onClick={() => goToSlide(index)}
//                                 aria-label={`Go to slide ${index + 1}`}
//                               />
//                             ))}
//                           </div>
//                         )}
//                       </div>
//                     </>
//                   ) : (
//                     <div className="flex items-center justify-center h-full">
//                       <p className="text-red-500 text-lg">
//                         No options selected!
//                       </p>
//                     </div>
//                   )}
//                 </div>
//               </div>
//             )}

//             {/* Footer with Navigation */}
//             <div className="flex justify-between items-center w-full p-3 md:p-4">
//               <div className="flex gap-2">
//                 <span
//                   className={`h-4 w-4 rounded-full transition-colors ${
//                     currentPage === 1 ? "bg-[#54B0AF]" : "bg-gray-300"
//                   }`}
//                 ></span>
//                 <span
//                   className={`h-4 w-4 rounded-full transition-colors ${
//                     currentPage === 2 ? "bg-[#54B0AF]" : "bg-gray-300"
//                   }`}
//                 ></span>
//               </div>

//               <div className="flex gap-2 md:gap-4">
//                 {currentPage === 1 ? (
//                   <button
//                     onClick={() => setCurrentPage(2)}
//                     disabled={selectedOptions.length === 0}
//                     className={`px-4 md:px-6 py-2 rounded-full flex items-center justify-center gap-2 text-xl md:text-2xl transition-all
//                       ${
//                         selectedOptions.length > 0
//                           ? "bg-[#54B0AF] text-white hover:bg-teal-600"
//                           : "bg-[#CACACA] text-gray-600 opacity-50 cursor-not-allowed"
//                       }`}
//                   >
//                     <FaArrowRight />
//                   </button>
//                 ) : (
//                   <div className="flex gap-2 md:gap-4">
//                     <button
//                       onClick={() => setCurrentPage(1)}
//                       className="px-4 md:px-6 py-2 bg-gray-200 hover:bg-gray-300 text-gray-600 rounded-full flex text-lg md:text-xl items-center gap-2 transition-all"
//                     >
//                       <FaArrowLeft />
//                     </button>

//                     <button
//                       onClick={handleSubmit}
//                       disabled={isSubmitting || !isSubmitEnabled}
//                       className={`px-4 md:px-6 py-2 md:py-3 rounded-full font-semibold transition-all text-sm md:text-base
//                         ${
//                           isSubmitting
//                             ? "bg-gray-400 cursor-not-allowed text-white"
//                             : isSubmitEnabled
//                             ? "bg-[#54B0AF] text-white hover:bg-teal-600"
//                             : "bg-[#CACACA] text-gray-600 opacity-50 cursor-not-allowed"
//                         }`}
//                     >
//                       {isSubmitting ? "Submitting..." : "Submit"}
//                     </button>
//                   </div>
//                 )}
//               </div>
//             </div>
//           </div>
//         </div>
//       )}
//     </>
//   );
// };

// export default ActionPopup;

// before 11-08-2025

// import React, { useState, useContext, useEffect } from "react";
// import { UserContext } from "../../../UserContext";
// import Conclusion from "./ConclusionPopup";
// import { FaCheck, FaArrowRight, FaArrowLeft } from "react-icons/fa";
// import Panda from "../../../assets/Panda4.png";
// import { Smile } from "lucide-react";
// import { motion } from "framer-motion";
// import { IoClose, IoRadioButtonOff } from "react-icons/io5";
// // import posthog from "posthog-js";
// import { usePostHog } from "posthog-js/react";
// import { Listbox } from "@headlessui/react";
// import { Check, ChevronDown } from "lucide-react";

// const ActionPopup = ({ summary, options, newSession, session_Goal }) => {
//   const [currentPage, setCurrentPage] = useState(1);
//   const [selectedOptions, setSelectedOptions] = useState([]);
//   const [optionList, setOptionList] = useState(
//     Array.isArray(options) ? options : []
//   );
//   const [isAllSelected, setIsAllSelected] = useState(false);
//   const [newOption, setNewOption] = useState("");
//   const [trackingFrequencies, setTrackingFrequencies] = useState({});
//   const [conclutionPopup, setConclutionPopup] = useState(false);
//   const [sessionGoal, setSessionGoal] = useState("");
//   const [actionData, setActionData] = useState("");
//   const [insightData, setInsightData] = useState("");
//   const [summeryData, setSummeryData] = useState("");
//   const [conclusionData, setConclusionData] = useState("");
//   const [thankYouData, setThankYouData] = useState("");
//   const [accountability, setAccountability] = useState({});
//   const apiBaseURL = process.env.REACT_APP_BACKEND_API_URL;
//   const [isSubmitting, setIsSubmitting] = useState(false);
//   const [insightFromUser, setInsightFromUser] = useState("");
//   const { user } = useContext(UserContext);

//   const accountabilityOptions = [
//     { value: "", label: "Select Accountability Partner" },
//     { value: "no-one", label: "I'll go solo – I trust myself" },
//     { value: "someone-else", label: "Someone special" },
//   ];

//   const handleOptionChange = (option) => {
//     setIsAllSelected(false);
//     if (selectedOptions.includes(option)) {
//       setSelectedOptions(selectedOptions.filter((item) => item !== option));
//       const newTracking = { ...trackingFrequencies };
//       delete newTracking[option];
//       setTrackingFrequencies(newTracking);

//       // Also remove accountability when option is deselected
//       const newAccountability = { ...accountability };
//       delete newAccountability[option];
//       setAccountability(newAccountability);
//     } else {
//       setSelectedOptions([...selectedOptions, option]);
//     }
//   };

//   const handleAccountabilityChange = (option, value) => {
//     setAccountability((prev) => ({
//       ...prev,
//       [option]: value,
//     }));
//   };

//   const posthog = usePostHog();
//   useEffect(() => {
//     console.log("useEffect triggered"); // Confirm useEffect runs
//     console.log(
//       posthog.capture("Action_popup", { focus: "what u want to focus on" })
//     );
//     console.log("posthog.capture executed", window.location.href); // Confirm capture is attempted
//   }, []);

//   const handleAddOption = () => {
//     const trimmedOption = newOption.trim();

//     if (
//       trimmedOption !== "" &&
//       !optionList.some(
//         (opt) => opt.toLowerCase() === trimmedOption.toLowerCase()
//       )
//     ) {
//       setOptionList([...optionList, trimmedOption]); // Add to options list
//       setSelectedOptions([...selectedOptions, trimmedOption]); // Auto-select new option
//       setNewOption(""); // Clear input field
//     }
//   };

//   const handleDeleteOption = (optionToDelete) => {
//     setOptionList(optionList.filter((option) => option !== optionToDelete));
//     setSelectedOptions(selectedOptions.filter((opt) => opt !== optionToDelete));

//     // Also remove from tracking frequencies and accountability
//     const newTracking = { ...trackingFrequencies };
//     delete newTracking[optionToDelete];
//     setTrackingFrequencies(newTracking);

//     const newAccountability = { ...accountability };
//     delete newAccountability[optionToDelete];
//     setAccountability(newAccountability);
//   };

//   const formattedSelection = selectedOptions
//     .map((option) => `${option} (${trackingFrequencies[option] || ""})`)
//     .join(" , ");

//   // Updated condition: Both frequency AND accountability must be selected for all options
//   const isSubmitEnabled =
//     selectedOptions.length > 0 &&
//     selectedOptions.every((option) => trackingFrequencies[option]) &&
//     selectedOptions.every(
//       (option) => accountability[option] && accountability[option] !== ""
//     );

//   const handleSubmit = async () => {
//     if (isSubmitting) return;
//     setIsSubmitting(true);
//     try {
//       const formattedResponse = selectedOptions.map((option) => ({
//         action: option,
//         trackingFrequency: trackingFrequencies[option] || "",
//         accountability: accountability[option]
//           ? accountability[option] === "someone-else"
//             ? {
//                 name:
//                   document.querySelector(`input[placeholder="Their name"]`)
//                     ?.value || "",
//                 email:
//                   document.querySelector(
//                     `input[placeholder="Their email (so we can nudge them gently)"]`
//                   )?.value || "",
//               }
//             : {
//                 name: "Myself",
//                 email: "placeholder@email.com",
//               }
//           : null,
//       }));
//       const payload = {
//         responselist: formattedResponse,
//         session_id: newSession,
//       };
//       console.log("Payload to send:", payload);
//       const response = await fetch(`${apiBaseURL}/action_responses`, {
//         method: "POST",
//         headers: {
//           "Content-Type": "application/json",
//         },
//         body: JSON.stringify(payload),
//       });
//       const data = await response.json();
//       if (response.ok) {
//         appendInSessionStep();
//         genrateConclution();
//       } else {
//         console.error("Error in submission:", data.detail);
//       }
//     } catch (error) {
//       console.error("Error during submission:", error);
//     }
//   };

//   const appendInSessionStep = async () => {
//     try {
//       const payload = {
//         coachee_response: formattedSelection,
//         focus_popup_required: 1,
//         record_from_focus_popup: 0,
//         selected_focus_choice: 400,
//         regenerate_with_intention: false,
//         focus_options: "",
//         alternate_focus_hypothesis: "",
//         conversation_summary: "",
//       };

//       const response = await fetch(
//         `${apiBaseURL}/users/${user.user_id}/session/${newSession}/step`,
//         {
//           method: "POST",
//           headers: {
//             "Content-Type": "application/json",
//           },
//           body: JSON.stringify(payload),
//         }
//       );
//       const data = await response.json();
//       if (response.ok) {
//         console.log(data);
//         setThankYouData(data.thank_you);
//         setConclusionData(data.conclusion);
//         setSummeryData(data.conclusion_summery);
//       } else {
//         console.error("Error in regeneration:", data.detail);
//       }
//     } catch (error) {
//       console.error("Error during regeneration:", error);
//     }
//   };

//   const genrateConclution = async () => {
//     try {
//       const response = await fetch(`${apiBaseURL}/conclusion/${newSession}`, {
//         method: "GET",
//         headers: {
//           "Content-Type": "application/json",
//         },
//       });
//       const data = await response.json();
//       if (response.ok) {
//         console.log(data);
//         setConclutionPopup(true);
//         setActionData(data.conclusion.session_actions);
//         setInsightData(data.conclusion.session_insights);
//         setSessionGoal(data.conclusion.session_goal);
//         setInsightFromUser(data.question_response);
//         // setSummeryData(data.conclusion_summery);
//       } else {
//         console.error("Error in submission:", data.detail);
//       }
//     } catch (error) {
//       console.error("Error during submission:", error);
//     }
//   };

//   return (
//     <>
//       {conclutionPopup ? (
//         <Conclusion
//           conclusion={conclusionData}
//           thankyou={thankYouData}
//           summary={summeryData}
//           action={actionData}
//           insight={insightData}
//           yourInsight={insightFromUser}
//           session_goal={session_Goal}
//           newSession={newSession}
//         />
//       ) : (
//         <div className="fixed inset-0 bg-gray-700 bg-opacity-80 flex justify-center items-center z-50 font-gotham">
//           <div className="bg-white mt-10 rounded-2xl shadow-lg w-[80%] md:w-[85%] lg:w-[70%] xl:w-[65%] lg:max-w-[900px] xl:max-w-[1000px] overflow-hidden">
//             <div className="flex gap-3 ml-4 pt-2">
//               {[...Array(3)].map((_, i) => (
//                 <div key={i} className="w-4 h-4 rounded-full"></div>
//               ))}
//             </div>
//             <>
//               {currentPage === 1 && (
//                 <div className="flex bg-white overflow-hidden">
//                   <div className="w-1/3 relative bg-gray-100 flex flex-col items-center justify-center">
//                     <motion.img
//                       src={Panda}
//                       alt="Feedback Panda"
//                       className="absolute bottom-0"
//                       initial={{ scale: 1 }}
//                       animate={{ scale: 1 }}
//                       exit={{ scale: 0.6, transition: { duration: 0.5 } }}
//                     />
//                   </div>

//                   {/* Right Section - Content */}
//                   <div className="w-2/3 h-[600px] p-4 overflow-y-auto">
//                     <h2 className="text-2xl font-medium text-gray-800">
//                       Why don't you choose the focus areas of improvement?
//                     </h2>
//                     <p className="text-gray-500 text-lg font-medium mt-1">
//                       (You can choose and add as many as you want.)
//                     </p>

//                     {/* Options */}
//                     <div className="mt-6 space-y-4">
//                       {optionList.map((option, index) => (
//                         <div
//                           key={index}
//                           className={`flex items-center justify-between p-4 rounded-lg border cursor-pointer transition-all relative
//                 ${
//                   selectedOptions.includes(option)
//                     ? "border-[#54B0AF] bg-gray-50"
//                     : "border-gray-300 bg-white hover:bg-gray-50"
//                 }`}
//                           onClick={() => handleOptionChange(option)}
//                         >
//                           <div className="flex items-center">
//                             <div
//                               className={`w-6 h-6 rounded-md flex items-center justify-center transition-colors
//                     ${
//                       selectedOptions.includes(option)
//                         ? "bg-[#54B0AF] text-white"
//                         : "bg-gray-200"
//                     }`}
//                             >
//                               {selectedOptions.includes(option) && (
//                                 <IoRadioButtonOff className="text-lg" />
//                               )}
//                             </div>
//                             <span className="ml-3 font-medium text-lg rounded-xl text-gray-900">
//                               {option}
//                             </span>
//                           </div>

//                           <button
//                             onClick={(e) => {
//                               e.stopPropagation();
//                               handleDeleteOption(option);
//                             }}
//                             className="absolute -top-3 -right-2 bg-white border border-gray-400 rounded-full text-gray-500 hover:text-red-600 transition-all"
//                           >
//                             <IoClose className="w-5 h-5" />
//                           </button>
//                         </div>
//                       ))}
//                     </div>
//                     <div className="mt-4">
//                       <input
//                         type="text"
//                         value={newOption}
//                         onChange={(e) => setNewOption(e.target.value)}
//                         className="w-full p-3 rounded-xl font-medium border border-[#54B0AF] text-lg placeholder-gray-400 placeholder:font-medium focus:outline-none focus:ring-1 focus:ring-[#54B0AF] focus:border-[#54B0AF]"
//                         placeholder="Something else? Please type here..."
//                       />
//                       <button
//                         onClick={handleAddOption}
//                         className="p-2 text-[#54B0AF] font-medium"
//                       >
//                         + Add Custom Action
//                       </button>
//                     </div>
//                   </div>
//                 </div>
//               )}

//               {currentPage === 2 && (
//                 <div
//                   key="page2"
//                   initial={{ opacity: 0, x: 20 }}
//                   animate={{ opacity: 1, x: 0 }}
//                   exit={{ opacity: 0, x: -20 }}
//                   className="flex flex-col md:flex-row bg-white overflow-hidden rounded-lg"
//                 >
//                   <motion.div
//                     initial={{ scale: 1 }}
//                     animate={{ scale: 0.9 }}
//                     transition={{ duration: 0.5 }}
//                     className="w-full md:w-1/3 bg-gray-200 border border-gray-300 flex items-end justify-start rounded-xl"
//                   >
//                     <img
//                       src={Panda}
//                       alt="Feedback Panda"
//                       className="w-40 md:w-48 lg:w-56 object-contain"
//                     />
//                   </motion.div>
//                   <div className="w-full md:w-2/3 px-4 h-[300px] md:h-[600px] overflow-y-auto overflow-x-hidden custom-scrollbar max-h-[80vh]">
//                     {selectedOptions.length > 0 ? (
//                       selectedOptions.map((option, index) => (
//                         <div key={index} className="mt-6">
//                           {/* Heading */}
//                           <h2 className="text-lg md:text-xl font-light text-gray-700">
//                             Let's create a plan for{" "}
//                             <span className="text-lg md:text-xl font-medium text-gray-900">
//                               {option}
//                             </span>{" "}
//                             which makes the most sense.
//                           </h2>

//                           {/* Frequency */}
//                           <p className="text-md md:text-lg font-medium text-gray-800 mt-2">
//                             How often will you do this?
//                           </p>
//                           <div className="flex flex-wrap gap-3 mt-2">
//                             {["Daily", "Weekly", "Monthly"].map((freq) => (
//                               <button
//                                 key={freq}
//                                 onClick={() =>
//                                   setTrackingFrequencies((prev) => ({
//                                     ...prev,
//                                     [option]: freq,
//                                   }))
//                                 }
//                                 className={`px-4 py-1 rounded-lg font-medium transition-all text-md md:text-md
//                     ${
//                       trackingFrequencies[option] === freq
//                         ? "bg-[#54B0AF] text-white"
//                         : "bg-white border border-gray-400 hover:bg-gray-200 text-gray-700"
//                     }`}
//                               >
//                                 {freq}
//                               </button>
//                             ))}
//                           </div>
//                           <p className="text-lg md:text-lg font-semibold text-gray-900 mt-3 mb-2">
//                             Accountability Partner
//                           </p>
//                           <Listbox
//                             value={accountability[option] || ""}
//                             onChange={(value) =>
//                               handleAccountabilityChange(option, value)
//                             }
//                           >
//                             <div className="relative mt-1">
//                               <Listbox.Button className="relative w-full cursor-default rounded-xl border text-[#CACACA] border-gray-300 bg-gray-50 py-2 pl-4 pr-10 text-left shadow-sm focus:outline-none focus:ring-1 focus:ring-black text-sm">
//                                 {accountabilityOptions.find(
//                                   (o) => o.value === accountability[option]
//                                 )?.label || "Select Accountability Partner"}
//                                 <span className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
//                                   <ChevronDown className="h-4 w-4 text-[#CACACA]" />
//                                 </span>
//                               </Listbox.Button>
//                               <Listbox.Options className="absolute z-10 mt-1 max-h-60 w-full overflow-auto rounded-xl bg-gray-100 py-1 text-sm shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none">
//                                 {accountabilityOptions.map((opt) => (
//                                   <Listbox.Option
//                                     key={opt.value}
//                                     value={opt.value}
//                                     className={({ active }) =>
//                                       `relative cursor-pointer select-none py-2 pl-10 pr-4 ${
//                                         active
//                                           ? "bg-gray-100 text-gray-900"
//                                           : "text-gray-700"
//                                       }`
//                                     }
//                                   >
//                                     {({ selected }) => (
//                                       <>
//                                         <span
//                                           className={`block truncate ${
//                                             selected
//                                               ? "font-medium"
//                                               : "font-normal"
//                                           }`}
//                                         >
//                                           {opt.label}
//                                         </span>
//                                         {selected ? (
//                                           <span className="absolute inset-y-0 left-0 flex items-center pl-3 text-[#54B0AF]">
//                                             <Check className="w-4 h-4" />
//                                           </span>
//                                         ) : null}
//                                       </>
//                                     )}
//                                   </Listbox.Option>
//                                 ))}
//                               </Listbox.Options>
//                             </div>
//                           </Listbox>

//                           {accountability[option] === "no-one" && (
//                             <p className="text-sm text-gray-500 mt-2 flex items-center gap-1">
//                               <Smile className="w-4 h-4 text-gray-500" /> No
//                               worries! We'll cheer you on with gentle reminders.
//                             </p>
//                           )}
//                           {accountability[option] === "someone-else" && (
//                             <div className="mt-2 flex flex-col gap-2">
//                               <input
//                                 type="text"
//                                 placeholder="Their name"
//                                 className="p-1 w-full rounded-lg border border-gray-300 text-md placeholder-gray-400 focus:ring-2 focus:ring-black"
//                                 required
//                               />
//                               <input
//                                 type="email"
//                                 placeholder="Their email (so we can nudge them gently)"
//                                 className="p-1 w-full rounded-lg border border-gray-300 text-md placeholder-gray-400 focus:ring-2 focus:ring-black"
//                                 required
//                               />
//                             </div>
//                           )}
//                         </div>
//                       ))
//                     ) : (
//                       <p className="text-red-500">No options selected!</p>
//                     )}
//                   </div>
//                 </div>
//               )}
//             </>

//             <div className="flex justify-between items-center w-full">
//               <div className="flex gap-2 ml-4">
//                 <span
//                   className={`h-4 w-4 rounded-full ${
//                     currentPage === 1 ? "bg-[#54B0AF]" : "bg-gray-300"
//                   }`}
//                 ></span>
//                 <span
//                   className={`h-4 w-4 rounded-full ${
//                     currentPage === 2 ? "bg-[#54B0AF]" : "bg-gray-300"
//                   }`}
//                 ></span>
//               </div>

//               <div className="flex gap-4 p-2">
//                 {currentPage === 1 ? (
//                   <button
//                     onClick={() => setCurrentPage(2)}
//                     disabled={selectedOptions.length === 0}
//                     className={`px-6 py-2 rounded-full flex items-center justify-center gap-2 text-2xl transition-all
//     ${
//       selectedOptions.length > 0
//         ? "bg-[#54B0AF] text-white hover:bg-teal-600"
//         : "bg-[#CACACA] text-gray-600 opacity-50 cursor-not-allowed"
//     }`}
//                   >
//                     <FaArrowRight />
//                   </button>
//                 ) : (
//                   <>
//                     <div className="p-2 flex flex-row gap-4">
//                       <button
//                         onClick={() => setCurrentPage(1)}
//                         className="px-6 py-2 bg-gray-200 hover:bg-gray-200 text-gray-600 rounded-full flex text-xl items-center gap-2 transition-all"
//                       >
//                         <FaArrowLeft />
//                       </button>

//                       <button
//                         onClick={handleSubmit}
//                         disabled={isSubmitting || !isSubmitEnabled}
//                         className={`px-6 py-3 rounded-full font-semibold transition-all
//     ${
//       isSubmitting
//         ? "bg-gray-400 cursor-not-allowed text-white"
//         : isSubmitEnabled
//         ? "bg-[#54B0AF] text-white hover:bg-teal-600"
//         : "bg-[#CACACA] text-gray-600 opacity-50 cursor-not-allowed"
//     }`}
//                       >
//                         {isSubmitting ? "Submitting..." : "Submit"}
//                       </button>
//                     </div>
//                   </>
//                 )}
//               </div>
//             </div>
//           </div>
//         </div>
//       )}
//     </>
//   );
// };

// export default ActionPopup;
